/**
 * Shared formatting for inventory change-log rows — used by both the Changes tab and
 * the vehicle history page so labels, colors, and money formatting never drift apart.
 */
import React from "react"

import { THEME } from "../../config/theme"

export const CHANGE_TYPE_COLORS: Record<string, string> = {
  arrival: THEME.colors.semantic.success,
  sold: THEME.colors.semantic.danger,
  unwind: THEME.colors.semantic.info,
  transfer: THEME.colors.semantic.warning,
  // A vendor dropped a value we had; we kept the last-known value (sticky enrichment).
  cleared: THEME.colors.semantic.warning,
  field_change: THEME.colors.neutral.light.textSecondary,
}

// Human labels for every field the backend can name in a change event (see
// backend/services/inventory/digest.py: _TRACKED_FIELDS, _ENRICH_TRACKED_FIELDS,
// _CLEAR_LOGGED_FIELDS). Both the Changes tab and the vehicle history page render
// through fieldLabel() so a raw snake_case key never reaches the operator.
export const FIELD_LABELS: Record<string, string> = {
  tekion_status: "Status",
  title_status: "Title status",
  marketable: "Marketable",
  recon_step: "Recon step",
  recon_notes: "Recon note",
  photos_count: "HomeNet photos",
  website_status: "HomeNet state",
  valuation_kbb: "KBB Lending",
  wholesale_status: "Wholesale status",
  appraiser: "Appraiser",
  vauto_notes: "vAuto note",
  website_live: "Live on site",
  website_price: "Website price",
  website_photos_count: "Site photos",
  marketed_at_lee_bros: "Wholesaled at Lee Bros",
  hn_dealer_cert: "HomeNet Dealer Cert",
  website_cert_sticker: "Certified sticker",
  price_invoice: "Invoice",
  price_internet: "Internet price",
  price_retail: "Retail price",
  price_msrp: "MSRP",
  price_wholesale: "Wholesale price",
  price_asking: "Asking price",
  price_acv: "ACV",
  valuation_mmr: "MMR",
}

// The label for a change's field. Falls back to a de-underscored version of the raw
// key so a newly-tracked field is still readable before it earns a curated label.
export function fieldLabel(field: string | null): string {
  if (!field) return ""
  return FIELD_LABELS[field] ?? field.replace(/_/g, " ")
}

// Fields whose change value renders as money (Tekion prices, vAuto valuations, and the
// live-website price).
function isMoneyField(field: string | null): boolean {
  return !!field && (field.startsWith("price") || field.startsWith("valuation") || field === "website_price")
}

export function formatChangeValue(field: string | null, value: string | null): string {
  if (value == null || value === "") return "—"
  if (field === "website_live" || field === "website_cert_sticker") return value // already stored "Yes" / "No"
  if (field === "marketed_at_lee_bros" || field === "hn_dealer_cert") {
    return value === "True" ? "Yes" : value === "False" ? "No" : value
  }
  if (isMoneyField(field)) {
    const numeric = Number(value)
    if (!Number.isNaN(numeric)) return `$${numeric.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  }
  return value
}

export function formatChangeTime(iso: string | null): string {
  if (!iso) return ""
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString()
}

export function ChangeTypeChip({ type }: { type: string }) {
  const color = CHANGE_TYPE_COLORS[type] ?? THEME.colors.neutral.light.textSecondary
  return (
    <span
      style={{
        color,
        border: `1px solid ${color}`,
        borderRadius: THEME.borderRadius.pill,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {type.replace("_", " ")}
    </span>
  )
}
